import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiX, FiUser, FiCalendar, FiCheck, FiMapPin, 
  FiMail, FiPhone, FiChevronRight, FiChevronLeft,
  FiPackage, FiClock, FiPlus, FiMinus, FiInfo, FiGift,
  FiTruck, FiHome, FiEdit3, FiImage, FiLayers, FiUsers,
  FiStar, FiZap, FiInstagram, FiSearch
} from 'react-icons/fi';
import { FaFacebook, FaTiktok, FaGoogle } from 'react-icons/fa';

// Helper function to get API URL - uses relative path in production
const getApiUrl = () => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname.includes('justasmile-events.com') || hostname.includes('app.justasmile')) {
      return '';
    }
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};

const API_URL = getApiUrl();

// Event types (Entreprise removed - separate client type exists for businesses)
const EVENT_TYPES = [
  { id: 'mariage', label: 'Mariage', emoji: '💍' },
  { id: 'anniversaire', label: 'Anniversaire', emoji: '🎂' },
  { id: 'bapteme', label: 'Baptême', emoji: '👶' },
  { id: 'soiree', label: 'Soirée privée', emoji: '🎉' },
  { id: 'autre', label: 'Autre', emoji: '✨' }
];

// Discovery sources
const DISCOVERY_SOURCES = [
  { id: 'instagram', label: 'Instagram', icon: FiInstagram },
  { id: 'facebook', label: 'Facebook', icon: FaFacebook },
  { id: 'tiktok', label: 'TikTok', icon: FaTiktok },
  { id: 'google', label: 'Google', icon: FaGoogle },
  { id: 'recommendation', label: 'Recommandation', icon: FiUsers },
  { id: 'event', label: 'Lors d\'un événement', icon: FiStar },
  { id: 'unknown', label: 'Je ne sais plus', icon: FiSearch }
];

// Duration options (master list)
const DURATIONS = [
  { hours: 2, label: '2 heures' },
  { hours: 3, label: '3 heures' },
  { hours: 4, label: '4 heures' },
  { hours: 6, label: '6 heures' },
  { hours: 24, label: 'Illimitée (Max 24H)' }
];

// Helper to get available durations from products variations
const getProductDurations = (product) => {
  if (!product?.variations?.length) return [];
  // Extract unique duration_hours from all variations
  const durations = product.variations
    .filter(v => v.is_active !== false)
    .map(v => v.duration_hours)
    .filter(d => d !== undefined && d !== null);
  // Map 24 to unlimited display
  return [...new Set(durations)];
};

// Machine metadata (badges, capacity info)
const MACHINE_METADATA = {
  'photobooth': {
    badge: null,
    capacity: null
  },
  'videobooth-80': {
    badge: null,
    capacity: 'de 1 à 3 personnes',
    diameter: '80cm'
  },
  'videobooth-100': {
    badge: null,
    capacity: 'de 1 à 5 personnes',
    diameter: '100cm'
  },
  'livre-dor': {
    badge: 'New',
    badgeStyle: 'ribbon',
    capacity: null
  }
};

// Helper to get machine metadata - also check product's capacity_label from DB
const getMachineMetadata = (product) => {
  const name = product.name?.toLowerCase() || '';
  const category = product.category?.toLowerCase() || '';
  
  // First check if product has its own capacity_label from DB
  let baseMetadata = {};
  
  if (category === 'livre-dor' || name.includes('livre')) {
    baseMetadata = MACHINE_METADATA['livre-dor'];
  } else if (name.includes('100') || (category === 'videobooth' && name.includes('100'))) {
    baseMetadata = MACHINE_METADATA['videobooth-100'];
  } else if (name.includes('80') || (category === 'videobooth' && name.includes('80'))) {
    baseMetadata = MACHINE_METADATA['videobooth-80'];
  } else if (category === 'photobooth') {
    baseMetadata = MACHINE_METADATA['photobooth'];
  }
  
  // Override with product's own capacity_label if available
  if (product.capacity_label) {
    return { ...baseMetadata, capacity: product.capacity_label };
  }
  
  return baseMetadata;
};

export default function BookingModalV2({ 
  isOpen, 
  onClose, 
  primaryColor = '#D4AF37', 
  siteId,
  // Pre-selection props for when clicking from pricing pages
  preSelectedProduct = null,
  preSelectedVariation = null,
  preSelectedDuration = null,
  preSelectedFormula = null
}) {
  // Steps: 1=Machines+Durée, 2=Formules, 3=Lieu+Accessibilité, 4=Coordonnées+Maquette, 5=Récap
  const [step, setStep] = useState(1);
  const totalSteps = 6;
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  
  // User auth
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  
  // Products data
  const [products, setProducts] = useState([]);
  const [matchingFormulas, setMatchingFormulas] = useState([]);
  
  // Selections
  const [selectedMachines, setSelectedMachines] = useState([]);
  const [selectedDuration, setSelectedDuration] = useState(null);
  const [selectedFormula, setSelectedFormula] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [videoboothSize, setVideoboothSize] = useState(null); // '80cm' or '100cm'
  
  // Delivery & Accessibility
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [deliveryPossible, setDeliveryPossible] = useState(true);
  const [deliveryMessage, setDeliveryMessage] = useState('');
  
  // Form data
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    // Client type: 'particulier' or 'entreprise'
    clientType: 'particulier',
    companyName: '',
    eventType: '',
    eventDate: '',
    eventTime: '',  // Heure de démarrage
    eventAddress: '',
    postalCode: '',
    city: '',
    // Accessibility
    hasElevator: false,
    hasStairs: false, // Nouvelle question: présence d'escaliers
    floorNumber: 0,
    accessNotes: '',
    // Contact on site (3 fields)
    contactFirstName: '',
    contactLastName: '',
    contactPhone: '',
    // Template/Maquette
    templateDescription: '',
    // General notes
    message: '',
    // Discovery source (optional)
    discoverySource: ''
  });
  
  // Calculated values
  const [totalPrice, setTotalPrice] = useState(0);
  const [depositAmount, setDepositAmount] = useState(0);
  const [savings, setSavings] = useState(0);
  
  // Helper function for safe toFixed - prevents crashes on NaN/null/undefined
  const safeToFixed = (value, decimals = 2) => {
    const num = Number(value);
    return (isNaN(num) ? 0 : num).toFixed(decimals);
  };
  
  // Payment settings (loaded from API)
  // Default to fixed_link with 100€ to match typical site configuration
  const [paymentSettings, setPaymentSettings] = useState({
    deposit_type: 'fixed_link',
    deposit_percentage: 30,
    deposit_fixed_amount: 100,
    deposit_fixed_link: true
  });
  
  // Calculate available durations based on selected machines
  // This returns the intersection of durations available for all selected machines
  const availableDurations = useMemo(() => {
    // If no machines selected, don't show any duration
    if (selectedMachines.length === 0 || products.length === 0) {
      return [];
    }
    
    // Get the selected product objects
    const selectedProducts = products.filter(p => selectedMachines.includes(p.id));
    if (selectedProducts.length === 0) return [];
    
    // For single machine, use ONLY product's actual variations
    if (selectedMachines.length === 1) {
      const product = selectedProducts[0];
      const productDurations = getProductDurations(product);
      
      if (productDurations.length > 0) {
        // Only show durations that actually exist in the product's variations
        const filtered = DURATIONS.filter(d => productDurations.includes(d.hours));
        return filtered;
      }
      return [];
    }
    
    // For multiple machines (combined formulas), find intersection of available durations
    const allProductDurations = selectedProducts.map(p => getProductDurations(p));
    
    // Find common durations across all selected products
    if (allProductDurations.every(d => d.length > 0)) {
      const commonDurations = allProductDurations.reduce((acc, curr) => 
        acc.filter(d => curr.includes(d)), allProductDurations[0]
      );
      return DURATIONS.filter(d => commonDurations.includes(d.hours));
    }
    
    // Fallback: show durations available in combined formulas
    return DURATIONS.filter(d => [3, 4, 6, 24].includes(d.hours));
  }, [selectedMachines, products]);
  
  // Reset selected duration if it's no longer available
  useEffect(() => {
    if (selectedDuration !== null && availableDurations.length > 0) {
      const isStillAvailable = availableDurations.some(d => d.hours === selectedDuration);
      if (!isStillAvailable) {
        setSelectedDuration(null);
      }
    }
  }, [availableDurations, selectedDuration]);
  
  // Reservation state
  const [reservationSubmitted, setReservationSubmitted] = useState(false);
  const [showAuthStep, setShowAuthStep] = useState(false); // Show auth step before submission
  const [pendingSubmit, setPendingSubmit] = useState(false); // Track if we're waiting for auth to submit
  
  // Account creation state
  const [showPasswordField, setShowPasswordField] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [creatingAccount, setCreatingAccount] = useState(false);
  const [accountCreated, setAccountCreated] = useState(false);
  const [accountError, setAccountError] = useState(null);
  const [accountExists, setAccountExists] = useState(false);
  const [loginPassword, setLoginPassword] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordResult, setForgotPasswordResult] = useState(null);
  
  // Handle forgot password
  const handleForgotPassword = async () => {
    if (!formData.email) return;
    
    setForgotPasswordLoading(true);
    setForgotPasswordResult(null);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email })
      });
      
      const data = await response.json();
      setForgotPasswordResult(data);
    } catch (err) {
      setForgotPasswordResult({ error: true, message: 'Erreur lors de la réinitialisation' });
    } finally {
      setForgotPasswordLoading(false);
    }
  };
  
  // Check if account exists when reservation is submitted
  const checkAccountExists = async (email) => {
    try {
      const response = await fetch(`${API_URL}/api/public/check-account?email=${encodeURIComponent(email)}`);
      if (response.ok) {
        const data = await response.json();
        setAccountExists(data.exists);
      }
    } catch (err) {
      console.log('Error checking account');
    }
  };
  
  // Handle login for existing account
  const handleLogin = async () => {
    if (!loginPassword) return;
    
    setLoggingIn(true);
    setAccountError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          password: loginPassword
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('token', data.access_token);
        localStorage.setItem('user', JSON.stringify(data.user));
        setAccountCreated(true);
        setIsLoggedIn(true);
        
        // If we were waiting to submit, do it now
        if (pendingSubmit) {
          setShowAuthStep(false);
          await handleSubmit();
        }
      } else {
        setAccountError('Mot de passe incorrect');
      }
    } catch (err) {
      setAccountError('Erreur de connexion');
    } finally {
      setLoggingIn(false);
    }
  };
  
  // Handle account creation
  const handleCreateAccount = async () => {
    if (newPassword.length < 6) return;
    
    setCreatingAccount(true);
    setAccountError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/public/create-client-account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          password: newPassword,
          first_name: formData.firstName,
          last_name: formData.lastName,
          phone: formData.phone
        })
      });
      
      if (response.ok) {
        // Auto-login after account creation
        const loginResponse = await fetch(`${API_URL}/api/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: formData.email,
            password: newPassword
          })
        });
        
        if (loginResponse.ok) {
          const data = await loginResponse.json();
          localStorage.setItem('token', data.access_token);
          localStorage.setItem('user', JSON.stringify(data.user));
          setIsLoggedIn(true);
        }
        
        setAccountCreated(true);
        setShowPasswordField(false);
        
        // If we were waiting to submit, do it now
        if (pendingSubmit) {
          setShowAuthStep(false);
          await handleSubmit();
        }
      } else {
        const error = await response.json();
        setAccountError(error.detail || 'Erreur lors de la création du compte');
      }
    } catch (err) {
      setAccountError('Erreur de connexion. Veuillez réessayer.');
    } finally {
      setCreatingAccount(false);
    }
  };
  
  // Check authentication
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const response = await fetch(`${API_URL}/api/auth/me`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const user = await response.json();
            setIsLoggedIn(true);
            setCurrentUser(user);
            setFormData(prev => ({
              ...prev,
              firstName: user.first_name || prev.firstName,
              lastName: user.last_name || prev.lastName,
              email: user.email || prev.email,
              phone: user.phone || prev.phone
            }));
          }
        } catch (err) {
          console.log('Not authenticated');
        }
      }
    };
    if (isOpen) {
      checkAuth();
      loadProducts();
      loadPaymentSettings();
    }
  }, [isOpen, preSelectedProduct, preSelectedDuration, preSelectedFormula, preSelectedVariation]);
  
  // Separate effect to handle pre-selected formula after products are loaded
  useEffect(() => {
    if (!isOpen || products.length === 0 || !preSelectedFormula) return;
    
    console.log('Applying pre-selected formula:', preSelectedFormula);
    
    // Parse duration from formula (e.g., "3h" -> 3, "24h max" or "Illimitée" -> 24)
    // Handle both string ("3h") and number (3) formats
    const rawDuration = preSelectedFormula.duration;
    const durationStr = (typeof rawDuration === 'number' ? `${rawDuration}h` : String(rawDuration || '')).toLowerCase();
    let durationHours = parseInt(durationStr) || 3;
    // For unlimited/24h max, use 24 (matches product variation duration_hours)
    if (durationStr.includes('24h') || durationStr.includes('max') || durationStr.includes('illimité')) {
      durationHours = 24;
    }
    console.log('Setting duration to:', durationHours);
    setSelectedDuration(durationHours);
    
    // Build extras list
    const allExtras = [...(preSelectedFormula.extras || []), ...(preSelectedFormula.allExtras || [])];
    
    // Set the formula selection for display
    setSelectedFormula({
      id: preSelectedFormula.formulaId,
      name: preSelectedFormula.optionName ? `${preSelectedFormula.formulaName} - ${preSelectedFormula.optionName}` : preSelectedFormula.formulaName,
      description: preSelectedFormula.duration + ' de location',
      basePrice: preSelectedFormula.price,
      originalPrice: preSelectedFormula.price,
      gifts: allExtras,
      includesLivreDor: false,
      livreDorPrice: 0
    });
    
    // Also select the corresponding machine/product
    if (preSelectedProduct && products.length > 0) {
      const matchedProduct = products.find(p => 
        p.id === preSelectedProduct || 
        p.name?.toLowerCase().includes(preSelectedProduct.toLowerCase()) ||
        p.category?.toLowerCase() === preSelectedProduct.toLowerCase()
      );
      if (matchedProduct) {
        // Find the variation matching the duration (with null check)
        const matchingVariation = matchedProduct.variations?.find(v => 
          v.duration_hours === durationHours || 
          (durationHours === 0 && v.duration_hours === 24)
        ) || null;
        // Store only the ID, not the full object
        setSelectedMachines([matchedProduct.id]);
        // Store the pre-selected variation separately for later use
        if (matchingVariation) {
          setSelectedFormula({
            id: matchingVariation.id,
            name: matchingVariation.name,
            basePrice: matchingVariation.price,
            duration: matchingVariation.duration_hours,
            durationLabel: matchingVariation.duration_label,
            prints: matchingVariation.prints_count || 0,
            models: matchingVariation.templates_count || 1,
            musicChoices: matchingVariation.music_count || 0,
            gifts: [
              ...(matchingVariation.km_offerts ? ['Frais kilométriques OFFERTS'] : []),
              ...(matchingVariation.usb_included ? ['Clé USB offerte'] : [])
            ],
            features: matchingVariation.features || [],
            description: matchingVariation.features?.[0] || matchingVariation.description || ''
          });
        }
      }
    }
    
    // Skip to step 3 (formula selection) since machine is already selected
    setStep(3);
  }, [isOpen, products, preSelectedFormula, preSelectedProduct]);
  
  // Load payment settings
  const loadPaymentSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/public/payment-settings`);
      if (response.ok) {
        const data = await response.json();
        console.log('Payment settings loaded:', data);
        setPaymentSettings(data);
      }
    } catch (err) {
      console.error('Error loading payment settings:', err);
    }
  };
  
  // Load products
  const loadProducts = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/public/products`);
      if (response.ok) {
        const data = await response.json();
        // API returns {products: [...]} object, extract the array
        const productsList = Array.isArray(data) ? data : (data.products || []);
        // Filter products that are active (check both 'active' and 'is_active' fields)
        // Exclude carte-cadeau and pack categories as they are not standalone machines
        const filteredProducts = productsList.filter(p => 
          (p.active === true || p.is_active === true) && 
          !['carte-cadeau', 'pack'].includes(p.category)
        );
        setProducts(filteredProducts);
        
        // Apply pre-selection if provided
        if (preSelectedProduct && filteredProducts.length > 0) {
          const matchedProduct = filteredProducts.find(p => 
            p.id === preSelectedProduct || 
            p.name?.toLowerCase().includes(preSelectedProduct.toLowerCase()) ||
            p.category?.toLowerCase() === preSelectedProduct.toLowerCase()
          );
          
          if (matchedProduct) {
            // Store only the ID, not the full object
            setSelectedMachines([matchedProduct.id]);
            
            // If duration is pre-selected, set it
            if (preSelectedDuration) {
              const durationHours = parseInt(preSelectedDuration) || 
                (preSelectedDuration.toLowerCase().includes('illimitée') ? 0 : 3);
              setSelectedDuration(durationHours);
            }
            
            // If product is pre-selected but NO formula, advance to step 2 (duration selection)
            // This improves UX when clicking "Réserver" from a presentation page
            if (!preSelectedFormula) {
              console.log('Pre-selected product without formula, advancing to step 2');
              setStep(2);
            }
          }
        }
        
        // Apply pre-selected formula from FormulasPage or TarifsPage
        if (preSelectedFormula) {
          console.log('Pre-selected formula detected:', preSelectedFormula);
          // Parse duration from formula (e.g., "3h" -> 3, "24h max" or "Illimitée" -> 24)
          // Handle both string ("3h") and number (3) formats
          const rawDur = preSelectedFormula.duration;
          const durationStr = (typeof rawDur === 'number' ? `${rawDur}h` : String(rawDur || '')).toLowerCase();
          let durationHours = parseInt(durationStr) || 3;
          if (durationStr.includes('24h') || durationStr.includes('max') || durationStr.includes('illimité')) {
            durationHours = 24; // Use 24 for unlimited (matches product variation)
          }
          console.log('Setting duration to:', durationHours);
          setSelectedDuration(durationHours);
          
          // Build extras list
          const allExtras = [...(preSelectedFormula.extras || []), ...(preSelectedFormula.allExtras || [])];
          const hasFreeMileage = allExtras.some(e => String(e).toLowerCase().includes('kilométrique') || String(e).toLowerCase().includes('km offert'));
          
          // Set the formula selection for display
          const durDisplay = typeof rawDur === 'number' ? `${rawDur}h` : rawDur;
          setSelectedFormula({
            id: preSelectedFormula.formulaId,
            name: preSelectedFormula.optionName ? `${preSelectedFormula.formulaName} - ${preSelectedFormula.optionName}` : preSelectedFormula.formulaName,
            description: durDisplay + ' de location',
            basePrice: preSelectedFormula.price,
            originalPrice: preSelectedFormula.price,
            gifts: allExtras,
            includesLivreDor: false,
            livreDorPrice: 0
          });
          
          // Also select the corresponding machine/product
          if (preSelectedProduct && filteredProducts.length > 0) {
            const matchedProduct = filteredProducts.find(p => 
              p.id === preSelectedProduct || 
              p.name?.toLowerCase().includes(preSelectedProduct.toLowerCase()) ||
              p.category?.toLowerCase() === preSelectedProduct.toLowerCase()
            );
            if (matchedProduct) {
              // Store only the ID, not the full object
              setSelectedMachines([matchedProduct.id]);
            }
          }
          
          // Skip to step 3 (location) since formula is already selected
          setStep(3);
        }
      }
    } catch (err) {
      console.error('Error loading products:', err);
    } finally {
      setLoading(false);
    }
  };
  
  // Calculate delivery fee based on postal code
  const calculateDeliveryFee = useCallback(async (postalCode) => {
    if (!postalCode || postalCode.length < 5) {
      setDeliveryFee(0);
      setDeliveryPossible(true);
      setDeliveryMessage('');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/delivery/calculate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ postal_code: postalCode })
      });
      
      if (response.ok) {
        const data = await response.json();
        setDeliveryFee(data.fee || 0);
        setDeliveryPossible(data.possible !== false);
        setDeliveryMessage(data.message || '');
      }
    } catch (err) {
      console.error('Error calculating delivery:', err);
    }
  }, []);
  
  useEffect(() => {
    if (formData.postalCode?.length >= 5) {
      calculateDeliveryFee(formData.postalCode);
    }
  }, [formData.postalCode, calculateDeliveryFee]);
  
  // Find matching formulas when machines selected
  const findMatchingFormulas = useCallback(async () => {
    if (selectedMachines.length === 0) {
      setMatchingFormulas([]);
      return;
    }
    
    // selectedMachines should now always contain IDs (strings)
    const selectedIds = selectedMachines.map(m => typeof m === 'string' ? m : (m?.id || m));
    const selectedProducts = products.filter(p => selectedIds.includes(p.id));
    
    if (selectedProducts.length === 0) {
      console.warn('No products found for selected machine IDs:', selectedIds);
      setMatchingFormulas([]);
      return;
    }
    
    // Build category list for API call
    const categories = selectedProducts.map(p => {
      const name = p.name?.toLowerCase() || '';
      const category = p.category?.toLowerCase() || '';
      
      if (category === 'livre-dor' || name.includes('livre')) return 'livre-dor';
      if (name.includes('100')) return 'videobooth-100';
      if (name.includes('80')) return 'videobooth-80';
      if (category === 'photobooth') return 'photobooth';
      return category;
    }).filter(Boolean);
    
    // If only ONE machine selected, use its variations directly
    if (selectedMachines.length === 1 && selectedProducts.length > 0) {
      const product = selectedProducts[0];
      const variations = product?.variations || [];
      
      // Convert variations to formula format
      const formulas = variations
        .filter(v => v.is_active !== false)
        .filter(v => {
          // Filter by duration if selected
          if (selectedDuration === null) return true;
          const vDuration = v.duration_hours || 0;
          // For unlimited (24h), only show unlimited variations
          if (selectedDuration === 24) return vDuration === 24;
          // Show variations with EXACT matching duration
          return vDuration === selectedDuration;
        })
        .map(v => ({
          id: v.id,
          name: v.name,
          basePrice: v.price,
          duration: v.duration_hours,
          durationLabel: v.duration_label,
          prints: v.prints_count || 0,
          models: v.templates_count || 1,
          musicChoices: v.music_count || 0,
          gifts: [
            ...(v.km_offerts ? ['Frais kilométriques OFFERTS'] : []),
            ...(v.usb_included ? ['Clé USB offerte'] : [])
          ],
          features: v.features || [],
          description: v.features?.[0] || v.description || ''
        }));
      
      setMatchingFormulas(formulas);
      return;
    }
    
    // Multiple machines selected - fetch combined formulas from API
    try {
      const response = await fetch(`${API_URL}/api/public/combined-formulas/match`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          categories: categories.sort(),
          duration_hours: selectedDuration 
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        const combinedFormulas = data.formulas || [];
        
        // Convert combined formulas to the expected format
        const formulas = combinedFormulas
          .filter(f => {
            if (selectedDuration === null) return true;
            const fDuration = f.duration_hours || 0;
            if (selectedDuration === 24) return fDuration === 24;
            // Show formulas with EXACT matching duration
            return fDuration === selectedDuration;
          })
          .map(f => ({
            id: f.id,
            name: f.name,
            basePrice: f.price,
            duration: f.duration_hours,
            durationLabel: f.duration_label,
            prints: f.prints_count || 0,
            models: f.templates_count || 1,
            musicChoices: f.music_count || 0,
            gifts: [
              ...(f.km_offerts ? ['Frais kilométriques OFFERTS'] : []),
              ...(f.usb_included ? ['Clé USB offerte'] : [])
            ],
            features: f.features || [],
            description: f.features?.[0] || `${f.duration_label} de location`,
            isCombined: true
          }));
        
        setMatchingFormulas(formulas);
      } else {
        console.error('Failed to fetch combined formulas');
        setMatchingFormulas([]);
      }
    } catch (err) {
      console.error('Error fetching combined formulas:', err);
      setMatchingFormulas([]);
    }
  }, [selectedMachines, selectedDuration, products]);
  
  useEffect(() => {
    findMatchingFormulas();
  }, [findMatchingFormulas]);
  
  // Calculate total price and savings
  const calculateTotal = useCallback(() => {
    let total = 0;
    let originalTotal = 0;
    
    if (selectedFormula) {
      // Ensure numeric values with fallback to 0
      total = Number(selectedFormula.basePrice) || 0;
      originalTotal = Number(selectedFormula.originalPrice) || total;
      
      // Add Livre d'Or if included in selection but not in formula
      if (selectedFormula.includesLivreDor) {
        total += Number(selectedFormula.livreDorPrice) || 0;
        originalTotal += Number(selectedFormula.livreDorOriginalPrice) || 0;
      }
    }
    
    // Add options
    selectedOptions.forEach(optId => {
      products.forEach(p => {
        const opt = p.options?.find(o => o.id === optId);
        if (opt) total += Number(opt.price) || 0;
      });
    });
    
    // Add delivery fee (unless formula includes free delivery OR 2+ machines OR unlimited formula)
    const formulaDuration = selectedFormula?.duration;
    const durationForCheck = typeof formulaDuration === 'number' ? `${formulaDuration}h` : String(formulaDuration || '');
    const isUnlimited = durationForCheck.toLowerCase().includes('illimit') || 
                        selectedFormula?.name?.toLowerCase().includes('illimit');
    const hasFreeDeliveryGift = selectedFormula?.gifts?.includes('Frais km offerts');
    const hasMultipleMachines = selectedMachines.length >= 2;
    
    if (!hasFreeDeliveryGift && !hasMultipleMachines && !isUnlimited) {
      total += Number(deliveryFee) || 0;
    }
    
    // Round to 2 decimal places to avoid floating point precision issues
    // Ensure we always set a number, never NaN
    const finalTotal = Math.round(total * 100) / 100;
    setTotalPrice(isNaN(finalTotal) ? 0 : finalTotal);
    
    // Calculate deposit based on settings (fixed or percentage)
    if (paymentSettings?.deposit_type === 'fixed_link' && paymentSettings?.deposit_fixed_amount) {
      setDepositAmount(Number(paymentSettings.deposit_fixed_amount) || 100);
    } else {
      const percentage = Number(paymentSettings?.deposit_percentage) || 30;
      const deposit = Math.round(total * (percentage / 100) * 100) / 100;
      setDepositAmount(isNaN(deposit) ? 0 : deposit);
    }
    
    const calculatedSavings = originalTotal > total ? originalTotal - total : 0;
    setSavings(isNaN(calculatedSavings) ? 0 : calculatedSavings);
  }, [selectedFormula, selectedOptions, deliveryFee, products, paymentSettings]);
  
  useEffect(() => {
    calculateTotal();
  }, [calculateTotal]);
  
  // Can proceed to next step?
  const canProceed = () => {
    switch (step) {
      case 1:
        return selectedMachines.length > 0;
      case 2:
        return selectedDuration !== null;
      case 3:
        return selectedFormula !== null;
      case 4:
        return formData.postalCode?.length >= 5 && formData.eventAddress && deliveryPossible;
      case 5:
        // Check company name if enterprise
        const companyValid = formData.clientType === 'entreprise' ? formData.companyName : true;
        // Event type not required for companies
        const eventTypeValid = formData.clientType === 'entreprise' ? true : formData.eventType;
        if (isLoggedIn) {
          return eventTypeValid && formData.eventDate && formData.eventTime && companyValid;
        }
        return formData.firstName && formData.lastName && formData.email && 
               formData.phone && eventTypeValid && formData.eventDate && formData.eventTime && companyValid;
      default:
        return true;
    }
  };
  
  // Handle "Valider" button click - check auth first
  const handleValidateClick = async () => {
    // If already logged in, submit directly
    if (isLoggedIn) {
      await handleSubmit();
      return;
    }
    
    // Check if account exists for this email
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/public/check-account?email=${encodeURIComponent(formData.email)}`);
      if (response.ok) {
        const data = await response.json();
        setAccountExists(data.exists);
      }
    } catch (err) {
      console.log('Error checking account');
    }
    setSubmitting(false);
    
    // Show auth step
    setShowAuthStep(true);
    setPendingSubmit(true);
  };
  
  // Submit reservation (called after auth is complete)
  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      // Get the first selected machine info
      const primaryProduct = products.find(p => p.id === selectedMachines[0]);
      
      // Build contact on site string
      const contactOnSite = formData.contactFirstName || formData.contactLastName || formData.contactPhone
        ? `${formData.contactFirstName} ${formData.contactLastName} - ${formData.contactPhone}`.trim()
        : '';
      
      const reservationData = {
        client_name: `${formData.firstName} ${formData.lastName}`.trim(),
        client_email: formData.email,
        client_phone: formData.phone,
        
        // Client type (particulier or entreprise)
        client_type: formData.clientType,
        company_name: formData.clientType === 'entreprise' ? formData.companyName : '',
        
        event_type: formData.eventType,
        event_date: formData.eventDate,
        event_time: formData.eventTime,  // Heure de démarrage
        event_address: `${formData.eventAddress}, ${formData.city}, ${formData.postalCode}`.trim(),
        postal_code: formData.postalCode,
        city: formData.city,
        
        // Product info for backend compatibility
        product_id: selectedMachines[0] || '',
        product_name: primaryProduct?.name || selectedFormula?.name || '',
        
        // Machines & Formula - convert to string for backend
        selected_machines_str: selectedMachines.map(id => {
          const product = products.find(p => p.id === id);
          return product?.name || '';
        }).filter(Boolean).join(', '),
        
        formula_id: selectedFormula?.id || '',
        formula_name: selectedFormula?.name || '',
        duration_hours: selectedDuration || 0,
        
        // Accessibility
        has_elevator: formData.hasElevator,
        has_stairs: formData.hasStairs, // Nouvelle question escaliers
        floor_number: formData.floorNumber,
        access_notes: formData.accessNotes || '',
        contact_on_site: contactOnSite,
        
        // Template
        template_description: formData.templateDescription || '',
        
        // Pricing
        total_price: totalPrice,
        deposit_amount: depositAmount,
        balance_amount: totalPrice - depositAmount,
        delivery_fee: deliveryFee,
        savings: savings,
        
        // Discovery source
        discovery_source: formData.discoverySource || '',
        
        // Notes (combine all notes)
        notes: [
          formData.message ? `Message: ${formData.message}` : '',
          formData.hasStairs ? '⚠️ ESCALIERS SUR LE LIEU DE L\'ÉVÉNEMENT' : '',
          formData.accessNotes ? `Accès: ${formData.accessNotes}` : '',
          contactOnSite ? `Contact sur place: ${contactOnSite}` : '',
          formData.templateDescription ? `Maquette souhaitée: ${formData.templateDescription}` : '',
          `Étage: ${formData.floorNumber}${formData.hasElevator ? ' (ascenseur)' : ' (pas d\'ascenseur)'}`,
          formData.discoverySource ? `Découverte: ${DISCOVERY_SOURCES.find(s => s.id === formData.discoverySource)?.label || formData.discoverySource}` : ''
        ].filter(Boolean).join('\n'),
        
        source: 'website'
      };
      
      const response = await fetch(`${API_URL}/api/public/reservations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reservationData)
      });
      
      console.log('Reservation response status:', response.status);
      
      if (response.ok) {
        console.log('Reservation submitted successfully');
        setReservationSubmitted(true);
        // Check if account exists for this email (only if not logged in)
        if (!isLoggedIn) {
          checkAccountExists(formData.email);
        }
      } else {
        const error = await response.json();
        // Handle Pydantic validation errors (array of objects)
        let errorMessage = 'Erreur lors de la réservation';
        if (error.detail) {
          if (Array.isArray(error.detail)) {
            errorMessage = error.detail.map(e => e.msg || String(e)).join(', ');
          } else if (typeof error.detail === 'string') {
            errorMessage = error.detail;
          } else {
            errorMessage = JSON.stringify(error.detail);
          }
        }
        setSubmitError(errorMessage);
      }
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitError('Erreur de connexion. Veuillez réessayer.');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Handle close
  const handleClose = () => {
    setStep(1);
    setSelectedMachines([]);
    setSelectedDuration(null);
    setSelectedFormula(null);
    setSelectedOptions([]);
    setReservationSubmitted(false);
    setSubmitError(null);
    setSavings(0);
    setFormData({
      firstName: currentUser?.first_name || '',
      lastName: currentUser?.last_name || '',
      email: currentUser?.email || '',
      phone: currentUser?.phone || '',
      clientType: 'particulier',
      companyName: '',
      eventType: '',
      eventDate: '',
      eventAddress: '',
      postalCode: '',
      city: '',
      hasElevator: false,
      hasStairs: false,
      floorNumber: 0,
      accessNotes: '',
      contactFirstName: '',
      contactLastName: '',
      contactPhone: '',
      templateDescription: '',
      message: '',
      discoverySource: ''
    });
    onClose();
  };
  
  if (!isOpen) return null;
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-0 sm:p-2 md:p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white rounded-none sm:rounded-2xl shadow-2xl w-full max-w-4xl h-full sm:h-auto sm:max-h-[95vh] md:max-h-[90vh] flex flex-col overflow-hidden"
          onClick={e => e.stopPropagation()}
        >
          {/* Header - Compact on mobile */}
          <div 
            className="p-3 md:p-5 text-white relative flex-shrink-0"
            style={{ background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}dd)` }}
          >
            <button
              onClick={handleClose}
              className="absolute top-2 right-2 md:top-4 md:right-4 p-2 hover:bg-white/20 rounded-full transition"
              data-testid="close-modal-btn"
            >
              <FiX className="w-5 h-5" />
            </button>
            
            <h2 className="text-lg md:text-xl font-bold pr-8">Réserver votre prestation</h2>
            <p className="text-white/80 text-xs md:text-sm mt-1">Étape {Math.min(step, totalSteps)} sur {totalSteps}</p>
            
            <div className="mt-2 md:mt-3 h-1 md:h-1.5 bg-white/20 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-white rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${(Math.min(step, totalSteps) / totalSteps) * 100}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
          </div>
          
          {/* Content - Scrollable with proper mobile height */}
          <div className="p-3 md:p-5 overflow-y-auto flex-1 min-h-0 overscroll-contain pb-2">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-4 border-gray-200" style={{ borderTopColor: primaryColor }} />
              </div>
            ) : (
              <>
                {/* Step 1: Select Machines + Duration */}
                {step === 1 && (
                  <div className="space-y-6">
                    <div className="text-center mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">Choisissez vos machines</h3>
                      <p className="text-sm text-gray-600">Sélectionnez une ou plusieurs machines</p>
                    </div>
                    
                    {/* Machine cards - 3 main machines */}
                    <div className="grid grid-cols-3 gap-2 sm:gap-4">
                      {(() => {
                        // Filter to show only 3 main machines: Photobooth, Livre d'Or, VideoBooth (generic)
                        const mainMachines = [];
                        const videobooth80 = products.find(p => p.name?.toLowerCase().includes('80'));
                        const videobooth100 = products.find(p => p.name?.toLowerCase().includes('100'));
                        
                        products.forEach(product => {
                          const name = product.name?.toLowerCase() || '';
                          // Skip pack and carte cadeau
                          if (name.includes('pack') || name.includes('carte cadeau')) return;
                          // Skip videobooth 80cm (we'll show a generic VideoBooth)
                          if (name.includes('80')) return;
                          // For VideoBooth 100cm, we'll use it as the generic VideoBooth display
                          mainMachines.push(product);
                        });
                        
                        return mainMachines.map(product => {
                          const isVideoBooth = product.name?.toLowerCase().includes('videobooth') || product.name?.toLowerCase().includes('vidéobooth');
                          const isSelected = isVideoBooth 
                            ? (selectedMachines.includes(videobooth80?.id) || selectedMachines.includes(videobooth100?.id))
                            : selectedMachines.includes(product.id);
                          const metadata = getMachineMetadata(product);
                          
                          // For VideoBooth, show generic name
                          const displayName = isVideoBooth ? 'VideoBooth 360°' : product.name;
                          const displayCapacity = isVideoBooth ? 'De 1 à 5 personnes' : metadata.capacity;
                          const displayPrice = isVideoBooth ? 'À partir de 349€' : `À partir de ${product.variations?.[0]?.price || 299}€`;
                          
                          return (
                            <button
                              key={product.id}
                              data-testid={`machine-${product.id}`}
                              onClick={() => {
                                if (isVideoBooth) {
                                  // For VideoBooth, show size selection
                                  if (isSelected) {
                                    // Deselect both sizes
                                    setSelectedMachines(prev => prev.filter(id => id !== videobooth80?.id && id !== videobooth100?.id));
                                    setVideoboothSize(null);
                                  } else {
                                    // Show size selection modal
                                    setVideoboothSize('selecting');
                                  }
                                } else {
                                  if (isSelected) {
                                    setSelectedMachines(prev => prev.filter(id => id !== product.id));
                                  } else {
                                    setSelectedMachines(prev => [...prev, product.id]);
                                  }
                                }
                              }}
                              className={`relative p-4 rounded-xl border-2 transition-all duration-200 text-left ${
                                isSelected 
                                  ? 'shadow-lg' 
                                  : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                              }`}
                              style={{ 
                                borderColor: isSelected ? primaryColor : undefined,
                                backgroundColor: isSelected ? `${primaryColor}08` : undefined,
                              }}
                            >
                              {/* Ribbon badge for "New" - positioned on the image */}
                              {metadata.badge && metadata.badgeStyle === 'ribbon' && (
                                <div className="absolute top-0 left-0 z-20 overflow-hidden w-16 h-16 sm:w-24 sm:h-24 pointer-events-none">
                                  <div className="absolute top-[8px] left-[-22px] sm:top-[12px] sm:left-[-32px] w-[80px] sm:w-[120px] bg-red-600 text-white text-[8px] sm:text-xs font-bold py-0.5 sm:py-1 text-center transform -rotate-45 shadow-md">
                                    {metadata.badge}
                                  </div>
                                </div>
                              )}
                              
                              {isSelected && (
                                <div 
                                  className="absolute -top-2 -right-2 w-7 h-7 rounded-full flex items-center justify-center text-white z-10"
                                  style={{ backgroundColor: primaryColor }}
                                >
                                  <FiCheck className="w-4 h-4" />
                                </div>
                              )}
                              
                              <div className="aspect-[4/3] sm:aspect-square rounded-lg overflow-hidden mb-2 sm:mb-3 bg-gray-100">
                                {product.images?.[0] ? (
                                  <img 
                                    src={product.images[0]} 
                                    alt={displayName}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <FiPackage className="w-10 h-10 text-gray-400" />
                                  </div>
                                )}
                              </div>
                              
                              <h4 className="font-semibold text-gray-900 text-xs sm:text-sm mb-0.5 sm:mb-1 line-clamp-1">{displayName}</h4>
                              {displayCapacity && (
                                <p className="text-[10px] sm:text-xs text-gray-500 items-center gap-1 mb-0.5 sm:mb-1 hidden sm:flex">
                                  <FiUsers className="w-3 h-3" />
                                  {displayCapacity}
                                </p>
                              )}
                              <p className="text-xs sm:text-sm font-medium" style={{ color: primaryColor }}>{displayPrice}</p>
                              
                              {/* Show selected size for VideoBooth */}
                              {isVideoBooth && videoboothSize && videoboothSize !== 'selecting' && isSelected && (
                                <p className="text-xs text-gray-600 mt-1 bg-gray-100 rounded px-2 py-1">
                                  Taille: {videoboothSize}
                                </p>
                              )}
                            </button>
                          );
                        });
                      })()}
                    </div>
                    
                    {/* VideoBooth Size Selection Modal */}
                    {videoboothSize === 'selecting' && (
                      <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
                        <div className="bg-white rounded-2xl p-6 max-w-md w-full">
                          <h4 className="text-lg font-semibold mb-4 text-center">Choisissez la taille du VideoBooth</h4>
                          <p className="text-sm text-gray-600 mb-4 text-center">La taille détermine le nombre de personnes pouvant participer simultanément</p>
                          
                          <div className="grid grid-cols-2 gap-4">
                            {/* 80cm option */}
                            <button
                              onClick={() => {
                                const vb80 = products.find(p => p.name?.toLowerCase().includes('80'));
                                if (vb80) {
                                  setSelectedMachines(prev => [...prev.filter(id => !products.find(p => p.id === id)?.name?.toLowerCase().includes('videobooth')), vb80.id]);
                                  setVideoboothSize('80cm');
                                }
                              }}
                              className="p-4 border-2 border-gray-200 rounded-xl hover:border-[#d4af37] hover:bg-[#d4af37]/5 transition-all"
                            >
                              <div className="text-2xl font-bold text-gray-900 mb-1">80cm</div>
                              <p className="text-sm text-gray-600">1 à 3 personnes</p>
                              <p className="text-sm font-medium text-[#d4af37] mt-2">À partir de 349€</p>
                            </button>
                            
                            {/* 100cm option */}
                            <button
                              onClick={() => {
                                const vb100 = products.find(p => p.name?.toLowerCase().includes('100'));
                                if (vb100) {
                                  setSelectedMachines(prev => [...prev.filter(id => !products.find(p => p.id === id)?.name?.toLowerCase().includes('videobooth')), vb100.id]);
                                  setVideoboothSize('100cm');
                                }
                              }}
                              className="p-4 border-2 border-gray-200 rounded-xl hover:border-[#d4af37] hover:bg-[#d4af37]/5 transition-all"
                            >
                              <div className="text-2xl font-bold text-gray-900 mb-1">100cm</div>
                              <p className="text-sm text-gray-600">1 à 5 personnes</p>
                              <p className="text-sm font-medium text-[#d4af37] mt-2">À partir de 399€</p>
                            </button>
                          </div>
                          
                          <button
                            onClick={() => setVideoboothSize(null)}
                            className="w-full mt-4 py-2 text-gray-500 hover:text-gray-700"
                          >
                            Annuler
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Step 2: Select Duration */}
                {step === 2 && (
                  <div className="space-y-6">
                    <div className="text-center mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">Choisissez la durée</h3>
                      <p className="text-sm text-gray-600">
                        Pour combien de temps souhaitez-vous réserver ?
                      </p>
                      {selectedMachines.length > 1 && availableDurations.length < DURATIONS.length && (
                        <p className="text-xs text-amber-600 mt-1">
                          Durées filtrées selon les machines sélectionnées
                        </p>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {availableDurations.map(dur => (
                        <button
                          key={dur.hours}
                          data-testid={`duration-${dur.hours}`}
                          onClick={() => setSelectedDuration(dur.hours)}
                          className={`p-4 rounded-xl border-2 text-center transition-colors duration-200 ${
                            selectedDuration === dur.hours
                              ? 'shadow-lg'
                              : 'border-gray-200 hover:border-gray-300 hover:shadow'
                          }`}
                          style={{ 
                            borderColor: selectedDuration === dur.hours ? primaryColor : undefined,
                            backgroundColor: selectedDuration === dur.hours ? `${primaryColor}15` : undefined,
                            transform: 'translateZ(0)' // Force GPU layer to prevent flickering
                          }}
                        >
                          <FiClock className={`w-6 h-6 mx-auto mb-2 ${selectedDuration === dur.hours ? '' : 'text-gray-400'}`} 
                            style={{ color: selectedDuration === dur.hours ? primaryColor : undefined }} />
                          <span className="text-base font-semibold block">{dur.label}</span>
                          {dur.note && (
                            <span className="block text-xs text-gray-500 mt-1">{dur.note}</span>
                          )}
                        </button>
                      ))}
                    </div>
                    
                    {/* Selected machines recap */}
                    <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500 mb-1">Machines sélectionnées :</p>
                      <div className="flex flex-wrap gap-2">
                        {selectedMachines.map(machineId => {
                          const machine = products.find(p => p.id === machineId);
                          return machine ? (
                            <span key={machineId} className="text-sm font-medium px-2 py-1 bg-white rounded border">
                              {machine.name}
                            </span>
                          ) : null;
                        })}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Step 3: Select Formula */}
                {step === 3 && (
                  <div className="space-y-5">
                    <div className="text-center mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">Choisissez votre formule</h3>
                      <p className="text-sm text-gray-600">
                        {selectedMachines.length > 1 
                          ? 'Formules combinées pour vos machines' 
                          : `Formules disponibles pour ${selectedDuration === 24 ? 'durée illimitée' : selectedDuration + 'h'}`}
                      </p>
                    </div>
                    
                    {matchingFormulas.length === 0 ? (
                      <div className="text-center py-8 bg-gray-50 rounded-xl">
                        <FiInfo className="w-10 h-10 mx-auto text-gray-400 mb-2" />
                        <p className="text-gray-600">Aucune formule disponible pour cette durée.</p>
                        <p className="text-sm text-gray-500">Essayez une durée différente.</p>
                      </div>
                    ) : (
                      <div className="grid gap-3">
                        {matchingFormulas.map(formula => {
                          const isSelected = selectedFormula?.id === formula.id;
                          const formulaSavings = formula.originalPrice ? formula.originalPrice - formula.basePrice : 0;
                          
                          return (
                            <button
                              key={formula.id}
                              data-testid={`formula-${formula.id}`}
                              onClick={() => setSelectedFormula(formula)}
                              className={`relative p-4 rounded-xl border-2 text-left transition-colors duration-200 ${
                                isSelected ? 'shadow-lg' : 'border-gray-200 hover:border-gray-300'
                              }`}
                              style={{ 
                                borderColor: isSelected ? primaryColor : undefined,
                                backgroundColor: isSelected ? `${primaryColor}08` : undefined,
                                transform: 'translateZ(0)'
                              }}
                            >
                              <div className="flex justify-between items-start">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <h4 className="font-semibold text-gray-900">{formula.name}</h4>
                                    {formula.originalPrice && (
                                      <span className="text-xs px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: primaryColor }}>
                                        -{Math.round((1 - formula.basePrice / formula.originalPrice) * 100)}%
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-sm text-gray-600 mt-1">{formula.description}</p>
                                  
                                  {/* Included items */}
                                  <div className="flex flex-wrap gap-2 mt-2">
                                    {formula.duration > 0 && (
                                      <span className="text-xs px-2 py-1 bg-gray-100 rounded-full flex items-center gap-1">
                                        <FiClock className="w-3 h-3" /> {formula.duration}h
                                      </span>
                                    )}
                                    {formula.duration === 0 && (
                                      <span className="text-xs px-2 py-1 bg-gray-100 rounded-full flex items-center gap-1">
                                        <FiClock className="w-3 h-3" /> Illimitée
                                      </span>
                                    )}
                                    {/* Show "Vidéos 360° illimitées" for videobooth formulas */}
                                    {formula.features?.some(f => f.toLowerCase().includes('vidéos 360° illimitées')) && (
                                      <span className="text-xs px-2 py-1 bg-gray-100 rounded-full">
                                        🎬 Vidéos 360° illimitées
                                      </span>
                                    )}
                                    {formula.prints > 0 && (
                                      <span className="text-xs px-2 py-1 bg-gray-100 rounded-full">
                                        📸 {formula.prints} impressions
                                      </span>
                                    )}
                                    {formula.models && (
                                      <span className="text-xs px-2 py-1 bg-gray-100 rounded-full">
                                        🎨 {formula.models} modèle{formula.models > 1 ? 's' : ''}
                                      </span>
                                    )}
                                    {/* Show music count */}
                                    {formula.musicChoices > 0 && (
                                      <span className="text-xs px-2 py-1 bg-gray-100 rounded-full">
                                        🎵 {formula.musicChoices} musique{formula.musicChoices > 1 ? 's' : ''}
                                      </span>
                                    )}
                                  </div>
                                  
                                  {/* Gifts */}
                                  {formula.gifts?.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mt-2">
                                      {formula.gifts.map((gift, idx) => (
                                        <span 
                                          key={idx} 
                                          className="text-xs px-2 py-1 rounded-full flex items-center gap-1 text-white"
                                          style={{ backgroundColor: primaryColor }}
                                        >
                                          <FiGift className="w-3 h-3" /> {gift}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                  
                                  {/* Savings highlight */}
                                  {formulaSavings > 0 && (
                                    <div className="mt-2 flex items-center gap-1 text-green-600">
                                      <FiZap className="w-4 h-4" />
                                      <span className="text-sm font-medium">Vous économisez {formulaSavings}€</span>
                                    </div>
                                  )}
                                </div>
                                
                                <div className="text-right ml-4">
                                  {formula.originalPrice && (
                                    <p className="text-sm text-gray-400 line-through">{formula.originalPrice}€</p>
                                  )}
                                  <p className="text-xl font-bold" style={{ color: primaryColor }}>{formula.basePrice}€</p>
                                </div>
                              </div>
                              
                              {isSelected && (
                                <div className="absolute top-3 right-3">
                                  <FiCheck className="w-5 h-5" style={{ color: primaryColor }} />
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Step 4: Location & Accessibility */}
                {step === 4 && (
                  <div className="space-y-5">
                    <div className="text-center mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">Lieu de l'événement</h3>
                      <p className="text-sm text-gray-600">Renseignez l'adresse et les accès</p>
                    </div>
                    
                    {/* Address fields */}
                    <div className="grid gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Code postal *
                        </label>
                        <input
                          type="text"
                          data-testid="postal-code-input"
                          value={formData.postalCode}
                          onChange={e => setFormData(prev => ({ ...prev, postalCode: e.target.value }))}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                          placeholder="13800"
                          maxLength={5}
                        />
                        
                        {/* Delivery info */}
                        {formData.postalCode?.length >= 5 && (
                          <div className={`mt-2 p-3 rounded-lg ${deliveryPossible ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                            <div className="flex items-center gap-2">
                              <FiTruck className={`w-4 h-4 ${deliveryPossible ? 'text-green-600' : 'text-red-600'}`} />
                              {deliveryPossible ? (
                                <span className="text-sm text-green-800">
                                  {(() => {
                                    const dur = selectedFormula?.duration;
                                    const durStr = typeof dur === 'number' ? `${dur}h` : String(dur || '');
                                    const isUnlimited = durStr.toLowerCase().includes('illimit') || 
                                                       selectedFormula?.name?.toLowerCase().includes('illimit');
                                    const hasFreeDeliveryGift = selectedFormula?.gifts?.includes('Frais km offerts');
                                    const hasMultipleMachines = selectedMachines.length >= 2;
                                    
                                    if (deliveryFee === 0) {
                                      return <span className="font-medium">Livraison gratuite !</span>;
                                    } else if (hasFreeDeliveryGift || hasMultipleMachines || isUnlimited) {
                                      return (
                                        <span>
                                          <span className="line-through text-gray-500">{safeToFixed(deliveryFee)}€</span>{' '}
                                          <span className="font-medium text-green-600">
                                            Offerts {hasMultipleMachines ? 'avec 2+ machines' : isUnlimited ? 'avec formule illimitée' : 'avec votre formule'} !
                                          </span>
                                        </span>
                                      );
                                    } else {
                                      return <span>Frais de livraison : <strong>{safeToFixed(deliveryFee)}€</strong></span>;
                                    }
                                  })()}
                                </span>
                              ) : (
                                <span className="text-sm text-red-800">
                                  Zone non desservie. Contactez-nous pour un devis.
                                </span>
                              )}
                            </div>
                            {deliveryMessage && (
                              <p className="text-xs text-gray-600 mt-1">{deliveryMessage}</p>
                            )}
                          </div>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Ville</label>
                          <input
                            type="text"
                            value={formData.city}
                            onChange={e => setFormData(prev => ({ ...prev, city: e.target.value }))}
                            className="w-full p-3 border border-gray-300 rounded-lg"
                            placeholder="Istres"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Adresse *</label>
                          <input
                            type="text"
                            data-testid="address-input"
                            value={formData.eventAddress}
                            onChange={e => setFormData(prev => ({ ...prev, eventAddress: e.target.value }))}
                            className="w-full p-3 border border-gray-300 rounded-lg"
                            placeholder="123 rue du lieu"
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Accessibility */}
                    <div className="border-t pt-5">
                      <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                        <FiHome className="w-4 h-4" />
                        Accessibilité du lieu
                      </h4>
                      
                      <div className="grid gap-4">
                        {/* Stairs question - IMPORTANT for all machines */}
                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                          <label className="flex items-center gap-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.hasStairs}
                              onChange={e => setFormData(prev => ({ ...prev, hasStairs: e.target.checked }))}
                              className="w-5 h-5 rounded"
                              style={{ accentColor: primaryColor }}
                              data-testid="has-stairs-checkbox"
                            />
                            <span className="text-sm font-medium text-amber-800">
                              Présence d'escaliers sur le lieu de l'événement ?
                            </span>
                          </label>
                          {formData.hasStairs && (
                            <p className="text-xs text-amber-600 mt-2 ml-8">
                              ⚠️ Nos équipes prendront contact avec vous pour évaluer les conditions d'accès.
                            </p>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-4">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.hasElevator}
                              onChange={e => setFormData(prev => ({ ...prev, hasElevator: e.target.checked }))}
                              className="w-5 h-5 rounded"
                              style={{ accentColor: primaryColor }}
                            />
                            <span className="text-sm">Ascenseur disponible</span>
                          </label>
                          
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600">Étage :</span>
                            <input
                              type="number"
                              min="0"
                              value={formData.floorNumber}
                              onChange={e => setFormData(prev => ({ ...prev, floorNumber: parseInt(e.target.value) || 0 }))}
                              className="w-16 p-2 border border-gray-300 rounded-lg text-center"
                            />
                          </div>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Particularités d'accès ou consignes
                          </label>
                          <textarea
                            value={formData.accessNotes}
                            onChange={e => setFormData(prev => ({ ...prev, accessNotes: e.target.value }))}
                            className="w-full p-3 border border-gray-300 rounded-lg resize-none"
                            rows={2}
                            placeholder="Ex: Entrée par le parking souterrain, code portail : 1234..."
                          />
                        </div>
                        
                        {/* Contact on site - 3 fields */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Personne à contacter sur place
                          </label>
                          <div className="grid grid-cols-3 gap-3">
                            <input
                              type="text"
                              value={formData.contactFirstName}
                              onChange={e => setFormData(prev => ({ ...prev, contactFirstName: e.target.value }))}
                              className="w-full p-2.5 border border-gray-300 rounded-lg text-sm"
                              placeholder="Prénom"
                            />
                            <input
                              type="text"
                              value={formData.contactLastName}
                              onChange={e => setFormData(prev => ({ ...prev, contactLastName: e.target.value }))}
                              className="w-full p-2.5 border border-gray-300 rounded-lg text-sm"
                              placeholder="Nom"
                            />
                            <input
                              type="tel"
                              value={formData.contactPhone}
                              onChange={e => setFormData(prev => ({ ...prev, contactPhone: e.target.value }))}
                              className="w-full p-2.5 border border-gray-300 rounded-lg text-sm"
                              placeholder="Téléphone"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Step 5: Contact Info & Template */}
                {step === 5 && (
                  <div className="space-y-5">
                    <div className="text-center mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">Vos coordonnées</h3>
                      <p className="text-sm text-gray-600">Pour vous recontacter et personnaliser votre prestation</p>
                    </div>
                    
                    {/* Client type selection */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Type de client *</label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, clientType: 'particulier' }))}
                          className={`p-3 rounded-lg border-2 text-center transition-colors duration-200 flex items-center justify-center gap-2 ${
                            formData.clientType === 'particulier' ? 'shadow-md' : 'border-gray-200 hover:border-gray-300'
                          }`}
                          style={{ 
                            borderColor: formData.clientType === 'particulier' ? primaryColor : undefined,
                            backgroundColor: formData.clientType === 'particulier' ? `${primaryColor}15` : undefined,
                            transform: 'translateZ(0)'
                          }}
                        >
                          <FiUser className="w-5 h-5" />
                          <span className="font-medium">Particulier</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, clientType: 'entreprise' }))}
                          className={`p-3 rounded-lg border-2 text-center transition-colors duration-200 flex items-center justify-center gap-2 ${
                            formData.clientType === 'entreprise' ? 'shadow-md' : 'border-gray-200 hover:border-gray-300'
                          }`}
                          style={{ 
                            borderColor: formData.clientType === 'entreprise' ? primaryColor : undefined,
                            backgroundColor: formData.clientType === 'entreprise' ? `${primaryColor}15` : undefined,
                            transform: 'translateZ(0)'
                          }}
                        >
                          <FiLayers className="w-5 h-5" />
                          <span className="font-medium">Entreprise / Association</span>
                        </button>
                      </div>
                    </div>
                    
                    {/* Company name (if enterprise) */}
                    {formData.clientType === 'entreprise' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Nom de l'entreprise / association *</label>
                        <input
                          type="text"
                          value={formData.companyName}
                          onChange={e => setFormData(prev => ({ ...prev, companyName: e.target.value }))}
                          className="w-full p-3 border border-gray-300 rounded-lg"
                          placeholder="Nom de votre structure"
                        />
                      </div>
                    )}
                    
                    {/* Personal info */}
                    <div className="grid gap-4">
                      {!isLoggedIn && (
                        <>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Prénom *</label>
                              <input
                                type="text"
                                value={formData.firstName}
                                onChange={e => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                                className="w-full p-3 border border-gray-300 rounded-lg"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                              <input
                                type="text"
                                value={formData.lastName}
                                onChange={e => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                                className="w-full p-3 border border-gray-300 rounded-lg"
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                              <input
                                type="email"
                                value={formData.email}
                                onChange={e => setFormData(prev => ({ ...prev, email: e.target.value }))}
                                className="w-full p-3 border border-gray-300 rounded-lg"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone *</label>
                              <input
                                type="tel"
                                value={formData.phone}
                                onChange={e => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                                className="w-full p-3 border border-gray-300 rounded-lg"
                              />
                            </div>
                          </div>
                        </>
                      )}
                      
                      {/* Event info - Hide event type for companies */}
                      {formData.clientType !== 'entreprise' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Type d'événement *</label>
                          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                            {EVENT_TYPES.map(type => (
                              <button
                                key={type.id}
                                type="button"
                                data-testid={`event-type-${type.id}`}
                                onClick={() => setFormData(prev => ({ ...prev, eventType: type.id }))}
                                className={`p-2 rounded-lg border-2 text-center transition-colors duration-200 ${
                                  formData.eventType === type.id
                                    ? 'shadow-md'
                                    : 'border-gray-200 hover:border-gray-300'
                                }`}
                                style={{ 
                                  borderColor: formData.eventType === type.id ? primaryColor : undefined,
                                  backgroundColor: formData.eventType === type.id ? `${primaryColor}15` : undefined,
                                  transform: 'translateZ(0)'
                                }}
                              >
                                <span className="text-xl">{type.emoji}</span>
                                <span className="block text-xs mt-1">{type.label}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Date de l'événement *</label>
                          <input
                            type="date"
                            data-testid="event-date-input"
                            value={formData.eventDate}
                            onChange={e => setFormData(prev => ({ ...prev, eventDate: e.target.value }))}
                            className="w-full p-3 border border-gray-300 rounded-lg"
                            min={new Date().toISOString().split('T')[0]}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Heure de démarrage *</label>
                          <div className="relative">
                            <input
                              type="time"
                              data-testid="event-time-input"
                              value={formData.eventTime}
                              onChange={e => setFormData(prev => ({ ...prev, eventTime: e.target.value }))}
                              className="w-full p-3 border border-gray-300 rounded-lg"
                              step="1800"
                            />
                          </div>
                          <p className="text-xs text-gray-500 mt-1">Heures populaires : 18h00, 19h00, 20h00</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Template description */}
                    <div className="border-t pt-5">
                      <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                        <FiImage className="w-4 h-4" />
                        Personnalisation de votre maquette
                      </h4>
                      <p className="text-xs text-gray-500 mb-3">
                        Décrivez ce que vous souhaitez voir apparaître autour de vos photos/vidéos. 
                        Vous pourrez finaliser et valider depuis votre espace client.
                      </p>
                      <textarea
                        value={formData.templateDescription}
                        onChange={e => setFormData(prev => ({ ...prev, templateDescription: e.target.value }))}
                        className="w-full p-3 border border-gray-300 rounded-lg resize-none"
                        rows={3}
                        placeholder="Ex: Thème champêtre, nos prénoms 'Marie & Jean', date '15 juin 2026', couleurs pastel..."
                      />
                    </div>
                    
                    {/* Additional notes */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Message ou demande particulière
                      </label>
                      <textarea
                        value={formData.message}
                        onChange={e => setFormData(prev => ({ ...prev, message: e.target.value }))}
                        className="w-full p-3 border border-gray-300 rounded-lg resize-none"
                        rows={2}
                        placeholder="Précisions, questions..."
                      />
                    </div>
                    
                    {/* Discovery source - optional */}
                    <div className="border-t pt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Comment nous avez-vous découvert ? <span className="text-gray-400 font-normal">(optionnel)</span>
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {DISCOVERY_SOURCES.map(source => {
                          const Icon = source.icon;
                          const isSelected = formData.discoverySource === source.id;
                          return (
                            <button
                              key={source.id}
                              type="button"
                              onClick={() => setFormData(prev => ({ 
                                ...prev, 
                                discoverySource: prev.discoverySource === source.id ? '' : source.id 
                              }))}
                              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-colors duration-200 ${
                                isSelected
                                  ? 'text-white shadow-md'
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                              style={{ 
                                backgroundColor: isSelected ? primaryColor : undefined,
                                transform: 'translateZ(0)'
                              }}
                            >
                              <Icon className="w-3.5 h-3.5" />
                              <span>{source.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Step 6: Summary */}
                {step === 6 && !reservationSubmitted && !showAuthStep && (
                  <div className="space-y-4">
                    <div className="text-center mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">Récapitulatif</h3>
                      <p className="text-sm text-gray-600">Vérifiez votre demande</p>
                    </div>
                    
                    {/* Error display */}
                    {submitError && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800 text-sm">
                        <strong>Erreur :</strong> {submitError}
                      </div>
                    )}
                    
                    {/* No formula selected error */}
                    {!selectedFormula && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-yellow-800 text-sm">
                        <strong>Attention :</strong> Aucune formule sélectionnée. Veuillez retourner en arrière et sélectionner une formule.
                      </div>
                    )}
                    
                    {/* Selection summary */}
                    {selectedFormula && (
                    <div className="bg-gray-50 rounded-xl p-4">
                      <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                        <FiPackage className="w-4 h-4" />
                        Votre formule
                      </h4>
                      
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-semibold">{selectedFormula?.name}</p>
                          <p className="text-sm text-gray-600">{selectedFormula?.description}</p>
                          <div className="flex flex-wrap gap-1 mt-2">
                            {(selectedFormula?.gifts || []).map((gift, idx) => (
                              <span key={idx} className="text-xs px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: primaryColor }}>
                                🎁 {gift}
                              </span>
                            ))}
                          </div>
                        </div>
                        <p className="font-bold text-lg" style={{ color: primaryColor }}>{selectedFormula?.basePrice}€</p>
                      </div>
                      
                      {/* Livre d'Or if included */}
                      {selectedFormula?.includesLivreDor && (
                        <div className="flex justify-between items-center mt-3 pt-3 border-t">
                          <span>+ Livre d'Or Vidéo</span>
                          <span className="font-medium">+{selectedFormula.livreDorPrice}€</span>
                        </div>
                      )}
                      
                      {deliveryFee > 0 && !selectedFormula?.gifts?.includes('Frais km offerts') && (
                        <div className="flex justify-between items-center mt-3 pt-3 border-t">
                          <span className="flex items-center gap-1"><FiTruck className="w-4 h-4" /> Livraison</span>
                          <span className="font-medium">+{safeToFixed(deliveryFee)}€</span>
                        </div>
                      )}
                      
                      {/* Savings highlight */}
                      {savings > 0 && (
                        <div className="flex justify-between items-center mt-3 pt-3 border-t text-green-600">
                          <span className="flex items-center gap-1"><FiZap className="w-4 h-4" /> Économie réalisée</span>
                          <span className="font-medium">-{safeToFixed(savings)}€</span>
                        </div>
                      )}
                      
                      <div className="flex justify-between items-center mt-3 pt-3 border-t-2 border-gray-300">
                        <span className="font-bold">Total</span>
                        <span className="text-xl font-bold" style={{ color: primaryColor }}>{safeToFixed(totalPrice)}€</span>
                      </div>
                      <div className="flex justify-between text-sm text-gray-600 mt-1">
                        <span>Acompte {paymentSettings?.deposit_type === 'fixed_link' ? '(fixe)' : `(${paymentSettings?.deposit_percentage || 30}%)`}</span>
                        <span>{safeToFixed(depositAmount)}€</span>
                      </div>
                    </div>
                    )}
                    
                    {/* Event info */}
                    <div className="bg-gray-50 rounded-xl p-4">
                      <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                        <FiCalendar className="w-4 h-4" />
                        Événement
                      </h4>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div><span className="text-gray-500">Date:</span> {formData.eventDate ? new Date(formData.eventDate).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : '-'}</div>
                        <div><span className="text-gray-500">Heure:</span> {formData.eventTime || '-'}</div>
                        <div><span className="text-gray-500">Type:</span> {EVENT_TYPES.find(t => t.id === formData.eventType)?.label || '-'}</div>
                        <div><span className="text-gray-500">Durée:</span> {selectedDuration === 0 ? 'Illimitée' : `${selectedDuration}h`}</div>
                        <div className="col-span-2"><span className="text-gray-500">Lieu:</span> {formData.eventAddress}, {formData.postalCode} {formData.city}</div>
                      </div>
                    </div>
                    
                    {/* Info box */}
                    <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                      <div className="flex gap-3">
                        <FiInfo className="w-5 h-5 text-blue-500 flex-shrink-0" />
                        <div className="text-sm text-blue-800">
                          <p className="font-medium mb-1">Prochaines étapes</p>
                          <ol className="list-decimal list-inside space-y-0.5 text-blue-700 text-xs">
                            <li>Nous validons votre demande sous 24h</li>
                            <li>Vous recevez le contrat à signer</li>
                            <li>Versement de l'acompte de {depositAmount}€</li>
                            <li>Solde le jour de l'événement</li>
                          </ol>
                        </div>
                      </div>
                    </div>
                    
                    {/* Caution Warning */}
                    <div className="bg-amber-50 rounded-xl p-4 border border-amber-300">
                      <div className="flex gap-3">
                        <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-xl">📝</span>
                        </div>
                        <div className="text-sm">
                          <p className="font-semibold text-amber-900 mb-1">Chèque de caution requis</p>
                          <p className="text-amber-800">
                            Un <strong>chèque de caution de 2 000€</strong> (non encaissé) sera demandé le jour de l'événement. 
                            Merci de prévoir ce chèque à l'ordre de <strong>BESSAHRAOUI MOUNIR</strong>.
                          </p>
                          <p className="text-amber-700 text-xs mt-2">
                            Ce chèque vous sera restitué après vérification du matériel.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Step 6.5: Authentication before submission */}
                {step === 6 && !reservationSubmitted && showAuthStep && (
                  <div className="space-y-4">
                    <div className="text-center mb-4">
                      <div 
                        className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                        style={{ backgroundColor: `${primaryColor}20` }}
                      >
                        <FiUser className="w-8 h-8" style={{ color: primaryColor }} />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {accountExists ? 'Connectez-vous pour continuer' : 'Créez votre compte'}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        {accountExists 
                          ? 'Un compte existe déjà avec cet email. Connectez-vous pour valider votre demande.'
                          : 'Créez un compte pour suivre votre réservation et accéder à votre espace client.'
                        }
                      </p>
                    </div>
                    
                    {/* Recap mini */}
                    <div className="bg-gray-50 rounded-xl p-3 text-sm">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Email</span>
                        <span className="font-medium">{formData.email}</span>
                      </div>
                      <div className="flex justify-between items-center mt-2 pt-2 border-t">
                        <span className="text-gray-600">Total</span>
                        <span className="font-bold" style={{ color: primaryColor }}>{safeToFixed(totalPrice)}€</span>
                      </div>
                    </div>
                    
                    {accountError && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-800 text-sm">
                        {accountError}
                      </div>
                    )}
                    
                    {accountExists ? (
                      /* Login form for existing account */
                      <div className="bg-white rounded-xl p-4 border border-gray-200 space-y-3">
                        <div>
                          <label className="text-sm text-gray-700 mb-1 block font-medium">Mot de passe</label>
                          <input
                            type="password"
                            value={loginPassword}
                            onChange={(e) => setLoginPassword(e.target.value)}
                            placeholder="Entrez votre mot de passe"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                            style={{ '--tw-ring-color': primaryColor }}
                            data-testid="auth-login-password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowForgotPassword(true)}
                            className="text-sm mt-2 hover:underline"
                            style={{ color: primaryColor }}
                          >
                            Mot de passe oublié ?
                          </button>
                        </div>
                        
                        {showForgotPassword && (
                          <div className="p-3 bg-gray-50 rounded-lg border">
                            <p className="text-xs text-gray-600 mb-2">
                              Un nouveau mot de passe sera envoyé à <strong>{formData.email}</strong>
                            </p>
                            {forgotPasswordResult && (forgotPasswordResult.temp_password || forgotPasswordResult.message) ? (
                              <div className={`text-xs p-2 rounded ${forgotPasswordResult.temp_password ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                                {forgotPasswordResult.temp_password ? (
                                  <>
                                    <p>Nouveau mot de passe :</p>
                                    <p className="font-mono font-bold text-sm mt-1">{forgotPasswordResult.temp_password}</p>
                                    <p className="mt-1 text-[10px]">Utilisez-le pour vous connecter ci-dessus</p>
                                  </>
                                ) : (
                                  <p>{forgotPasswordResult.message}</p>
                                )}
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={handleForgotPassword}
                                disabled={forgotPasswordLoading}
                                className="w-full px-3 py-2 text-xs text-white rounded font-medium disabled:opacity-50"
                                style={{ backgroundColor: primaryColor }}
                              >
                                {forgotPasswordLoading ? 'Envoi...' : 'Réinitialiser mon mot de passe'}
                              </button>
                            )}
                          </div>
                        )}
                        
                        <button
                          onClick={handleLogin}
                          disabled={loggingIn || !loginPassword}
                          className="w-full px-4 py-3 text-white rounded-lg font-semibold flex items-center justify-center gap-2 disabled:opacity-50 transition-all"
                          style={{ backgroundColor: primaryColor }}
                          data-testid="auth-login-submit"
                        >
                          {loggingIn ? (
                            <>
                              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                              Connexion...
                            </>
                          ) : (
                            <>
                              <FiCheck className="w-5 h-5" />
                              Se connecter et valider
                            </>
                          )}
                        </button>
                      </div>
                    ) : (
                      /* Account creation form */
                      <div className="bg-white rounded-xl p-4 border border-gray-200 space-y-3">
                        <div>
                          <label className="text-sm text-gray-700 mb-1 block font-medium">
                            Créez un mot de passe
                          </label>
                          <input
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="Minimum 6 caractères"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                            style={{ '--tw-ring-color': primaryColor }}
                            data-testid="auth-create-password"
                          />
                          {newPassword && newPassword.length < 6 && (
                            <p className="text-xs text-red-500 mt-1">Le mot de passe doit contenir au moins 6 caractères</p>
                          )}
                        </div>
                        
                        <button
                          onClick={handleCreateAccount}
                          disabled={creatingAccount || newPassword.length < 6}
                          className="w-full px-4 py-3 text-white rounded-lg font-semibold flex items-center justify-center gap-2 disabled:opacity-50 transition-all"
                          style={{ backgroundColor: primaryColor }}
                          data-testid="auth-create-submit"
                        >
                          {creatingAccount ? (
                            <>
                              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                              Création en cours...
                            </>
                          ) : (
                            <>
                              <FiCheck className="w-5 h-5" />
                              Créer mon compte et valider
                            </>
                          )}
                        </button>
                        
                        <p className="text-xs text-gray-500 text-center">
                          En créant un compte, vous pourrez suivre vos réservations et accéder à votre historique.
                        </p>
                      </div>
                    )}
                    
                    {/* Back button */}
                    <button
                      onClick={() => { setShowAuthStep(false); setPendingSubmit(false); }}
                      className="w-full px-4 py-2 text-gray-600 text-sm hover:text-gray-800 flex items-center justify-center gap-2"
                    >
                      <FiChevronLeft className="w-4 h-4" />
                      Retour au récapitulatif
                    </button>
                  </div>
                )}
                
                {/* Confirmation */}
                {step === 6 && reservationSubmitted && (
                  <div className="text-center py-8">
                    <div 
                      className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                      style={{ backgroundColor: `${primaryColor}20` }}
                    >
                      <FiCheck className="w-8 h-8" style={{ color: primaryColor }} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Demande envoyée !</h3>
                    <p className="text-gray-600 max-w-md mx-auto text-sm">
                      Nous vous recontacterons très rapidement pour confirmer votre réservation.
                    </p>
                    
                    <div className="bg-gray-50 rounded-xl p-4 mt-4 max-w-sm mx-auto text-left">
                      <p className="text-sm text-gray-600 mb-2">
                        Email de confirmation envoyé à <strong>{formData.email}</strong>
                      </p>
                      <div className="text-sm space-y-1">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Total</span>
                          <span className="font-semibold">{safeToFixed(totalPrice)}€</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Acompte</span>
                          <span>{safeToFixed(depositAmount)}€</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Success message - user is now logged in */}
                    {isLoggedIn && (
                      <div className="mt-4 p-4 bg-green-50 rounded-xl max-w-sm mx-auto border border-green-200">
                        <div className="flex items-center gap-2">
                          <FiCheck className="w-5 h-5 text-green-600" />
                          <p className="text-sm font-semibold text-green-800">
                            Connecté en tant que {formData.email}
                          </p>
                        </div>
                        <p className="text-xs text-green-700 mt-2">
                          Vous pouvez suivre votre réservation dans votre espace client.
                        </p>
                        <button
                          onClick={() => window.location.href = '/mon-compte'}
                          className="mt-3 w-full px-4 py-2 text-sm font-medium rounded-lg border border-green-300 text-green-700 hover:bg-green-100 transition"
                        >
                          Accéder à mon espace client
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
          
          {/* Footer - Compact on mobile, safe area padding for iOS */}
          <div className="p-3 md:p-4 pb-6 sm:pb-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center flex-shrink-0 gap-2">
            {step > 1 && !reservationSubmitted && !showAuthStep ? (
              <button
                onClick={() => setStep(s => s - 1)}
                className="flex items-center gap-1 px-4 py-2 text-gray-600 hover:text-gray-900 transition text-sm"
                data-testid="back-btn"
              >
                <FiChevronLeft className="w-4 h-4" />
                Retour
              </button>
            ) : <div />}
            
            {step >= 4 && !reservationSubmitted && (
              <div className="text-center">
                <p className="text-xs text-gray-500">Total</p>
                <p className="text-lg font-bold" style={{ color: primaryColor }}>{safeToFixed(totalPrice)}€</p>
                {savings > 0 && (
                  <p className="text-xs text-green-600">-{safeToFixed(savings)}€ d'économie</p>
                )}
              </div>
            )}
            
            {step < 6 ? (
              <div className="flex flex-col items-end">
                <button
                  onClick={() => setStep(s => s + 1)}
                  disabled={!canProceed()}
                  className="flex items-center gap-1 px-5 py-2 text-white rounded-lg font-medium text-sm transition disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: primaryColor }}
                  data-testid="continue-btn"
                >
                  Continuer
                  <FiChevronRight className="w-4 h-4" />
                </button>
              </div>
            ) : !reservationSubmitted && !showAuthStep ? (
              <button
                onClick={handleValidateClick}
                disabled={submitting}
                className="flex items-center gap-1 px-5 py-2 text-white rounded-lg font-medium text-sm transition disabled:opacity-50"
                style={{ backgroundColor: primaryColor }}
                data-testid="submit-btn"
              >
                {submitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Vérification...
                  </>
                ) : (
                  <>
                    <FiCheck className="w-4 h-4" />
                    Valider ma demande
                  </>
                )}
              </button>
            ) : reservationSubmitted ? (
              <button
                onClick={handleClose}
                className="px-5 py-2 text-white rounded-lg font-medium text-sm"
                style={{ backgroundColor: primaryColor }}
              >
                Fermer
              </button>
            ) : null}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
